import { Combobox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { DetailedHTMLProps, LabelHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";

export type SimpleComboboxProps = {
  loading?: boolean;
  disabled?: boolean;
  selectedOptionName: string | undefined;
  availableOptions: { name: string; Icon?: any; disabled?: boolean }[];
  optionsDisabledText?: string;
  checkChange?: (optionName: string | undefined) => boolean;
  onChange?: (optionName: string | undefined) => any;
  labelProps?: DetailedHTMLProps<LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;
  inputProps?: React.ComponentProps<typeof Combobox.Input> & { readOnly?: boolean };
  showIcon?: boolean;
  addQueryToOptions?: boolean;
};

export default function SimpleCombobox({
  loading,
  disabled,
  selectedOptionName,
  availableOptions,
  optionsDisabledText,
  checkChange,
  onChange: onChangeExternal,
  labelProps,
  inputProps,
  showIcon = true,
  addQueryToOptions = false,
}: SimpleComboboxProps) {
  const isRemoved = useRef(false);
  useEffect(() => {
    isRemoved.current = false;
    return () => {
      isRemoved.current = true;
    };
  }, []);

  const resetChangedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    // Prevent setting state after the component is unmounted
    () => () => clearTimeout(resetChangedTimer.current as any),
    []
  );

  const [state, setState] = useState({
    selectedOptionName: undefined as string | undefined,
    initSelectedOptionName: undefined as string | undefined,
    query: "",
  });

  useEffect(() => {
    if (loading) return;
    setState((c) => {
      if (c.initSelectedOptionName === selectedOptionName) return c;
      return {
        ...c,
        selectedOptionName,
        initSelectedOptionName: selectedOptionName,
      };
    });
  }, [loading, selectedOptionName]);

  const onChange = useCallback(
    (optionName: string | null) => {
      if (optionName == null) return;
      if (optionName === state.selectedOptionName) return;
      if (checkChange && !checkChange(optionName)) {
        return;
      }

      setState((c) => ({
        ...c,
        selectedOptionName: optionName,
        query: "",
      }));

      if (onChangeExternal) {
        onChangeExternal(optionName);
      }
    },
    [state, setState, checkChange, onChangeExternal]
  );

  const allOptions = useMemo(() => {
    const _allOptions = [];
    if (addQueryToOptions && state.query && !availableOptions.find((o) => o.name === state.query)) {
      _allOptions.push({ name: state.query });
    }
    if (
      selectedOptionName &&
      selectedOptionName !== state.query &&
      !availableOptions.find((o) => o.name === selectedOptionName)
    ) {
      _allOptions.push({ name: selectedOptionName });
    }
    _allOptions.push(...availableOptions);
    return _allOptions;
  }, [availableOptions, addQueryToOptions, state.query, selectedOptionName]);

  const filteredOptions = useMemo(() => {
    if (!state.query) return allOptions;
    return allOptions.filter((o) => o.name.toLowerCase().includes(state.query.toLowerCase()));
  }, [allOptions, state.query]);

  const selectedOption = useMemo(() => {
    const _selectedOptionName = state.selectedOptionName ?? selectedOptionName;
    return allOptions.find((o) => o.name === _selectedOptionName);
  }, [allOptions, state.selectedOptionName, selectedOptionName]);

  return (
    <Combobox
      immediate
      as="div"
      value={selectedOption?.name}
      onChange={onChange}
      className="w-full"
      disabled={loading || disabled}
    >
      {labelProps && <label className="mb-1 block text-sm font-medium text-gray-700" {...labelProps} />}
      <div className="flex items-center relative">
        <div className="relative flex-1 rounded-md shadow-xs">
          {showIcon && selectedOption && selectedOption.Icon ? (
            <Combobox.Button className="absolute top-[8px] left-[12px] w-5 h-auto">
              <selectedOption.Icon className="h-full w-full text-gray-800" aria-hidden="true" />
            </Combobox.Button>
          ) : (
            <></>
          )}
          <Combobox.Input
            {...inputProps}
            className={classNames(
              showIcon && "pl-[44px]",
              "w-full rounded-md border-0 py-1.5 pr-12 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
              loading ? "animate-pulse bg-gray-200" : disabled ? "bg-gray-200/70" : "bg-white",
              inputProps?.className
            )}
            onChange={(event) => {
              setState((c) => ({ ...c, query: event.target.value }));
            }}
            onBlur={(_event: any) => {
              setState((c) => ({ ...c, query: "" }));
            }}
            displayValue={(optionName: string) => optionName}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
          {filteredOptions.length > 0 && (
            <Combobox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/10 focus:outline-hidden sm:text-sm">
              {filteredOptions.map((o) => (
                <Combobox.Option
                  key={o.name}
                  value={o.name}
                  disabled={Boolean(o.disabled)}
                  className={({ active, disabled }) =>
                    classNames(
                      "relative cursor-default select-none py-2 pl-3 pr-9",
                      active ? "bg-indigo-600 text-white" : "text-gray-900",
                      disabled && "opacity-50 cursor-not-allowed text-gray-400"
                    )
                  }
                >
                  {({ active, selected, disabled }) => (
                    <>
                      <div className="flex items-center">
                        {!showIcon ? null : o.Icon ? (
                          <o.Icon className={classNames("w-5 h-auto", disabled && "opacity-50")} aria-hidden="true" />
                        ) : (
                          <div className="w-5 h-auto" aria-hidden="true" />
                        )}
                        <span className={classNames(showIcon && "ml-3", "truncate", selected && "font-semibold")}>
                          {o.name}
                        </span>
                      </div>

                      {disabled ? (
                        <span
                          className={classNames(
                            "absolute inset-y-0 right-0 flex items-center pr-4 text-xs sm:text-sm",
                            "text-black"
                          )}
                        >
                          {optionsDisabledText}
                        </span>
                      ) : selected ? (
                        <span
                          className={classNames(
                            "absolute inset-y-0 right-0 flex items-center pr-4",
                            active ? "text-white" : "text-indigo-600"
                          )}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          )}
        </div>
      </div>
    </Combobox>
  );
}
